import React, { useEffect, useState, useCallback } from "react";
import Accordion from "react-bootstrap/Accordion";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DownloadIcon from '@mui/icons-material/Download';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import CircularProgress from '@mui/material/CircularProgress';
import { Badge, Button, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { syncStatus } from "../../store/slices/syncStatus";
import { downloadFile } from "../../utils/downloadUtils";

const SyncStatus = ({ currentActiveTab }) => {
  const [countdown, setCountdown] = useState(10);
  const [rotateIcon, setRotateIcon] = useState(false);
  const dispatch = useDispatch();
  const { statusTable, isSyncCompleted } = useSelector(state => state.syncStatus);
  const savedData = useSelector(state => state.config.data);
  const prodConfigId = useSelector(state => state.productMapping.configId);

  const fetchSyncStatus = useCallback(() => {
    if (savedData) {
      dispatch(syncStatus(savedData.struConfigId));
    } else if (prodConfigId) {
      dispatch(syncStatus(prodConfigId));
    }
  }, [savedData, prodConfigId, dispatch]);

  useEffect(() => {
    if (isSyncCompleted !== "true") {
      fetchSyncStatus(); // Initial fetch

      const countdownIntervalId = setInterval(() => {
        setCountdown(prev => {
          if (prev === 0) {
            setRotateIcon(true);
            setTimeout(() => setRotateIcon(false), 1000);
            fetchSyncStatus();
            return 10;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownIntervalId);
    }
  }, [currentActiveTab, isSyncCompleted, fetchSyncStatus]);

  const handleRefreshClick = () => {
    setRotateIcon(true);
    fetchSyncStatus();
    setCountdown(10);
    setTimeout(() => setRotateIcon(false), 1000);
  };

  const SyncRow = ({ sync }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [hasClicked, setHasClicked] = useState(false);

    const handleCancelSync = async (cancelUrl) => {
      if (hasClicked || isLoading || !cancelUrl) return;

      setIsLoading(true);
      setHasClicked(true);

      try {
        const response = await fetch(`/api/syncCancel?url=${encodeURIComponent(cancelUrl)}`, {
          method: 'GET',
        });

        if (!response.ok) throw new Error("Cancel Sync Failed");

        // Refresh status after successful cancellation
        setTimeout(fetchSyncStatus, 1000);
      } catch (error) {
        console.error('Sync cancellation failed', error);
      } finally {
        setIsLoading(false);
      }
    };

    const isActive = (sync.status === "IN_PROGRESS" || sync.status === "SUBMITTED") &&
      !hasClicked &&
      sync.cancelUrl;

    return (
      <button
        disabled={!isActive}
        onClick={() => handleCancelSync(sync.cancelUrl)}
        style={{
          background: "none",
          border: "none",
          borderRadius: "5px"
        }}
        title={isActive ? "Cancel sync" : "Cannot cancel completed sync"}
      >
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <DisabledByDefaultIcon
            className={!isActive ? "InactiveCancel" : "ActiveCancel"}
            style={{
              fontSize: '32px',
              color: isActive ? '#dc3545' : '#6c757d',
              opacity: isActive ? 1 : 0.5,
              cursor: isActive ? 'pointer' : 'default',
              borderRadius: "20px"
            }}
          />
        )}
      </button>
    );
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "COMPLETED":
        return 'success-status';
      case "COMPLETED_ERROR":
        return 'error-status';
      case "SUBMITTED":
      case "IN_PROGRESS":
        return 'inprogress-status';
      case "CANCELLED":
        return 'cancelled-status';
      default:
        return '';
    }
  };

  const formatStatusText = (status) => {
    switch (status) {
      case "COMPLETED_ERROR":
        return "COMPLETED WITH ERROR";
      case "IN_PROGRESS":
        return "IN PROGRESS";
      default:
        return status;
    }
  };

  const syncName = (name) =>{
    if(name == "PRODUCT MAPPING" || name == "COLLECTION MAPPING"){
      return name
    }
    return name+" "+"SYNC";
  }

  return (
    <Accordion defaultActiveKey="0" alwaysOpen className="mt-5">
      <Accordion.Item eventKey="0" className="my-3">
        <Accordion.Header>Sync Status Dashboard</Accordion.Header>
        <Accordion.Body className="p-4">
          <div className="countdown-refresh">
            {isSyncCompleted !== "true" && (
              <div className="text-muted">Refresh status in {countdown} seconds</div>
            )}
            <AutorenewIcon
              className={rotateIcon ? "rotate-icon" : ""}
              onClick={handleRefreshClick}
              style={{ cursor: 'pointer' }}
            />
          </div>

          <Table responsive className="text-center">
            <thead className="thead-light">
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Status</th>
                <th>Details</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Errors</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {statusTable.length > 0 ? (
                statusTable.map((sync) => (
                  <tr key={sync.struJobId}>
                    <td>{sync.struJobId}</td>
                    <td>{syncName(sync.type)}</td>
                    <td>
                      <Badge className={getStatusBadgeClass(sync.status)}>
                        {formatStatusText(sync.status)}
                      </Badge>
                    </td>
                    <td>
                      <div dangerouslySetInnerHTML={{ __html: sync.jobDetails }} />
                      {sync.summaryUrl !== "" &&
                        <Button
                          variant="primary"
                          size="sm"
                          className="summary-btn action"
                          title='Download report for sync summary'
                          onClick={() => downloadFile(sync.summaryUrl)}
                        >
                          <Badge bg="transparent" className="p-2 border border-success text-dark">
                            <DownloadIcon className="me-1 fs-6 rounded" />Summary report
                          </Badge>
                        </Button>

                      }
                    </td>
                    <td>{sync.startTime}</td>
                    <td>{sync.endTime || '-'}</td>
                    <td>
                      {sync.errorUrl ? (
                        <Button
                          size="sm"
                          onClick={() => downloadFile(sync.errorUrl)}
                          className="download-button"
                        >
                          Download
                        </Button>
                      ) : 'None'}
                    </td>
                    <td>
                      <SyncRow sync={sync} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No sync jobs found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default SyncStatus;